import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import DashboardLayout from '../../components/layout/DashboardLayout';
import DataTable from '../../components/common/DataTable';
import Modal from '../../components/common/Modal';
import FormField from '../../components/common/FormField';
import Pagination from '../../components/common/Pagination';
import StatusBadge from '../../components/common/StatusBadge';
import { Loader, ErrorBanner, SuccessBanner } from '../../components/common/Feedback';
import { useAuth } from '../../context/AuthContext';
import * as projectsApi from '../../api/projects';
import * as clientsApi from '../../api/clients';
import * as employeesApi from '../../api/employees';
import { formatDate } from '../../utils/format';

const PRIORITY_OPTIONS = ['Low', 'Medium', 'High', 'Urgent'].map((v) => ({ value: v, label: v }));
const STATUS_OPTIONS = ['Not Started', 'In Progress', 'Waiting for Client', 'Under Review', 'Completed'].map((v) => ({ value: v, label: v }));

const emptyForm = { projectName: '', clientId: '', description: '', startDate: '', dueDate: '', priority: 'Medium', status: 'Not Started', teamMemberIds: [] };

export default function Projects() {
  const { actor } = useAuth();
  const canManage = actor.role === 'super_admin' || actor.role === 'admin';

  const [rows, setRows] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [clientOptions, setClientOptions] = useState([]);
  const [employeeOptions, setEmployeeOptions] = useState([]);

  const load = useCallback(() => {
    setLoading(true);
    projectsApi
      .listProjects({ page, status: statusFilter || undefined })
      .then((res) => {
        setRows(res.data);
        setPagination(res.pagination);
      })
      .catch((err) => setError(err.response?.data?.message || 'Failed to load projects.'))
      .finally(() => setLoading(false));
  }, [page, statusFilter]);

  useEffect(() => {
    load();
  }, [load]);

  const openCreate = async () => {
    setForm(emptyForm);
    if (clientOptions.length === 0) {
      const res = await clientsApi.listClients({ limit: 500 });
      setClientOptions(res.data.map((c) => ({ value: c.id, label: c.clientName })));
    }
    if (employeeOptions.length === 0) {
      const res = await employeesApi.listEmployees({ limit: 500 });
      setEmployeeOptions(res.data);
    }
    setShowForm(true);
  };

  const toggleTeamMember = (id) => {
    setForm((prev) => ({
      ...prev,
      teamMemberIds: prev.teamMemberIds.includes(id) ? prev.teamMemberIds.filter((x) => x !== id) : [...prev.teamMemberIds, id],
    }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setError('');
    try {
      await projectsApi.createProject(form);
      setSuccess('Project created.');
      setShowForm(false);
      load();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to create project.');
    }
  };

  const columns = [
    { key: 'projectName', label: 'Project', render: (r) => <Link to={`/projects/${r.id}`}>{r.projectName}</Link> },
    { key: 'client', label: 'Client', render: (r) => r.client?.clientName || '-' },
    { key: 'priority', label: 'Priority', render: (r) => <StatusBadge status={r.priority} /> },
    { key: 'status', label: 'Status', render: (r) => <StatusBadge status={r.status} /> },
    { key: 'dueDate', label: 'Due Date', render: (r) => formatDate(r.dueDate) },
    { key: 'team', label: 'Team', render: (r) => (r.team || []).map((t) => t.name).join(', ') || '-' },
  ];

  return (
    <DashboardLayout title="Projects">
      <div className="page-header">
        <div>
          <h1 className="heading">Projects</h1>
          <p className="page-subtext">Audit engagements currently being tracked.</p>
        </div>
        {canManage && <button type="button" className="btn" onClick={openCreate}>New Project</button>}
      </div>

      <ErrorBanner message={error} />
      <SuccessBanner message={success} />

      <div className="filter-bar">
        <select value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}>
          <option value="">All Statuses</option>
          {STATUS_OPTIONS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
        </select>
      </div>

      {loading ? (
        <Loader text="Loading projects..." />
      ) : (
        <>
          <DataTable columns={columns} rows={rows} getRowKey={(r) => r.id} emptyMessage="No projects found." />
          <Pagination pagination={pagination} onPageChange={setPage} />
        </>
      )}

      {showForm && (
        <Modal
          title="New Project"
          onClose={() => setShowForm(false)}
          footer={
            <>
              <button type="button" className="btn btn-secondary" onClick={() => setShowForm(false)}>Cancel</button>
              <button type="submit" form="project-form" className="btn">Create Project</button>
            </>
          }
        >
          <form id="project-form" onSubmit={handleSave}>
            <div className="form-grid single-column">
              <FormField label="Project Name" value={form.projectName} onChange={(e) => setForm({ ...form, projectName: e.target.value })} required />
              <FormField label="Client" type="select" options={clientOptions} value={form.clientId} onChange={(e) => setForm({ ...form, clientId: e.target.value })} required />
              <FormField label="Description" type="textarea" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
              <FormField label="Start Date" type="date" value={form.startDate} onChange={(e) => setForm({ ...form, startDate: e.target.value })} />
              <FormField label="Due Date" type="date" value={form.dueDate} onChange={(e) => setForm({ ...form, dueDate: e.target.value })} />
              <FormField label="Priority" type="select" options={PRIORITY_OPTIONS} value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })} />
              <div className="form-field">
                <label>Assign Team Members</label>
                <div style={{ maxHeight: 180, overflowY: 'auto', border: '1px solid var(--color-border)', borderRadius: 6, padding: 10 }}>
                  {employeeOptions.map((emp) => (
                    <label key={emp.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 0' }}>
                      <input type="checkbox" style={{ width: 'auto' }} checked={form.teamMemberIds.includes(emp.id)} onChange={() => toggleTeamMember(emp.id)} />
                      {emp.name}
                    </label>
                  ))}
                </div>
              </div>
            </div>
          </form>
        </Modal>
      )}
    </DashboardLayout>
  );
}
